import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * SearchableSelect — a drop-in replacement for shadcn <Select> when the list is long
 * enough that scrolling hurts (customers, users, members, coaches, scripts, …).
 *
 * Props:
 *   options    : Array of `{ value, label, hint?, disabled? }`
 *   value      : selected value (string) or "" for none
 *   onChange   : (value: string) => void
 *   placeholder: shown when value is empty
 *   emptyLabel : label for the "None / clear" row (pass `null` to hide)
 *   allowClear : show an X in the trigger to clear (default: true when emptyLabel is set)
 *   testid     : root data-testid; children get `${testid}-input`, `${testid}-item-{value}`
 */
export function SearchableSelect({
  options = [],
  value = "",
  onChange,
  placeholder = "Select…",
  emptyLabel = "— none —",
  allowClear,
  disabled = false,
  className = "",
  contentClassName = "",
  testid = "searchable-select",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = useMemo(() => options.find((o) => o.value === value) || null, [options, value]);
  const clearable = allowClear ?? Boolean(emptyLabel);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = `${o.label || ""} ${o.hint || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  const pick = (v) => {
    onChange?.(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger
        disabled={disabled}
        className={cn(
          "inline-flex items-center justify-between gap-2 w-full rounded-md border border-[#262626] bg-[#0a0a0a] px-3 py-2 text-sm text-left transition-colors",
          "hover:border-[#3a3a3a] focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:opacity-50",
          !selected && "text-zinc-500",
          className
        )}
        data-testid={testid}
      >
        <span className="truncate flex-1">
          {selected ? (
            <span className="text-zinc-100">
              {selected.label}
              {selected.hint && <span className="text-zinc-500 ml-2">· {selected.hint}</span>}
            </span>
          ) : (
            placeholder
          )}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {clearable && selected && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); pick(""); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(""); } }}
              className="p-0.5 rounded-full text-zinc-500 hover:text-zinc-100 hover:bg-white/10 cursor-pointer"
              data-testid={`${testid}-clear`}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronsUpDown className="h-3.5 w-3.5 text-zinc-500" />
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className={cn(
          "p-0 border border-[#262626] bg-[#0f0f0f] text-zinc-100 shadow-xl w-[--radix-popover-trigger-width] max-h-72 flex flex-col",
          contentClassName
        )}
      >
        <div className="flex items-center gap-2 border-b border-[#1f1f1f] px-3 py-2">
          <Search className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type to search…"
            className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-zinc-600"
            data-testid={`${testid}-input`}
          />
          <span className="text-[10px] text-zinc-600 tabular-nums">{filtered.length}</span>
        </div>
        <div className="overflow-y-auto py-1">
          {clearable && emptyLabel && !query && (
            <Option
              key="__clear"
              option={{ value: "", label: emptyLabel }}
              selected={!value}
              onPick={pick}
              testid={`${testid}-item-clear`}
            />
          )}
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-zinc-500">No match for &quot;{query}&quot;</div>
          ) : (
            (filtered || []).map((opt) => (
              <Option
                key={opt.value || opt.label}
                option={opt}
                selected={opt.value === value}
                onPick={pick}
                testid={`${testid}-item-${opt.value}`}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Option({ option, selected, onPick, testid }) {
  return (
    <button
      type="button"
      disabled={option.disabled}
      onClick={() => onPick(option.value)}
      className={cn(
        "w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors",
        "hover:bg-[#161616] focus:bg-[#161616] focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed",
        selected && "bg-blue-500/10 text-blue-100"
      )}
      data-testid={testid}
    >
      <Check className={cn("h-3.5 w-3.5 shrink-0", selected ? "text-emerald-400" : "text-transparent")} />
      <div className="flex-1 min-w-0">
        <div className="truncate">{option.label}</div>
        {option.hint && <div className="text-[10px] text-zinc-500 truncate">{option.hint}</div>}
      </div>
    </button>
  );
}
